// SideCart.js

import React from "react";
import { Trans, useTranslation } from "react-i18next";
import CurrencyMarketSelect from "./CurrencyMarketSelect";
import { appendCheckoutReturnParams } from "../utils/purchaseRedirects";
import { appendShopifyCheckoutLocale } from "../utils/shopifyLocaleRouting";
import { getCartValueBucket, trackAnalyticsEvent } from "../utils/analytics";
import { publicAssetUrl } from "../utils/publicAssets";

const SHOPIFY_PRODUCT_PAGE_URL = "https://shop.lukkey.com/products/lukkey";
const SHOPIFY_CHECKOUT_BASE_URL = "https://shop.lukkey.com/cart";
const lukkeyWalletImage = publicAssetUrl(
  "/assets/video/home_hero_webp_1080/frame_0040.webp",
);

const normalizeVariantId = (variantId) => {
  if (!variantId) return "";
  const raw = String(variantId).trim();
  if (!raw) return "";
  const gidMatch = raw.match(/\/ProductVariant\/(\d+)$/);
  if (gidMatch?.[1]) return gidMatch[1];
  const numericMatch = raw.match(/^(\d+)$/);
  return numericMatch?.[1] || "";
};

const getColorDot = (color) => {
  const raw = String(color || "").toLowerCase();
  if (raw.includes("black") || raw.includes("黑")) return "#111111";
  if (raw.includes("silver") || raw.includes("银") || raw.includes("銀"))
    return "#cfd2d8";
  return "#b8ad9a";
};

const formatMoney = (price, currency, locale) => {
  const amount = Number(price || 0);
  const code = String(currency || "").trim().toUpperCase();
  if (!code) {
    return amount.toLocaleString(locale, { maximumFractionDigits: 2 });
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${code}${amount.toLocaleString(locale, { maximumFractionDigits: 2 })}`;
  }
};

const isReserveItem = (item) =>
  String(item?.cartKey || "").startsWith("reserve::");

function CartPriceCallout({ t }) {
  return (
    <div className="mb-4 rounded-2xl border border-[#e8e1d2] bg-[#f6efe2] p-4 text-black">
      <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#CCB68C]">
        {t("lukkey.pricing.launchEyebrow", {
          defaultValue: "Launch pricing",
        })}
      </p>
      <div className="mt-2 flex flex-wrap items-end gap-x-2 gap-y-1">
        <div className="text-sm leading-6 text-neutral-500">
          <Trans
            t={t}
            i18nKey="lukkey.pricing.inlineRich"
            defaults="Kickstarter price <original>{{kickstarterPrice}}</original> Reserve for {{depositPrice}} now and pay <launch>{{launchPrice}}</launch> at launch"
            values={{
              kickstarterPrice: "$299",
              depositPrice: "$1",
              launchPrice: "$279",
            }}
            components={{
              original: <span className="text-sm text-neutral-500 line-through" />,
              launch: (
                <span className="text-[24px] font-semibold leading-none text-black" />
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function SideCart({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  variantOptions = [],
  onAddVariant,
  selectedCountry = "US",
  countryOptions = [],
  onCountryChange,
}) {
  const { t } = useTranslation();
  const getItemPrice = (item) => Number.parseFloat(item.price || 0) || 0;
  const getItemCurrency = (item) => item.currency || "USD";
  const getItemLocale = (item) => item.locale;
  const getItemImage = (item) => item.image || lukkeyWalletImage;
  const itemCount = cartItems.reduce(
    (acc, item) => acc + (isReserveItem(item) ? 1 : item.quantity || 1),
    0,
  );
  const cartTotal = cartItems.reduce(
    (acc, item) => acc + getItemPrice(item) * (item.quantity || 1),
    0,
  );
  const cartCurrency = cartItems[0] ? getItemCurrency(cartItems[0]) : "USD";
  const cartLocale = cartItems[0] ? getItemLocale(cartItems[0]) : undefined;
  const hasReserve = cartItems.some(isReserveItem);

  React.useEffect(() => {
    if (!isOpen) return;
    const cartSummary = {
      cart_item_count: itemCount,
      cart_value_bucket: getCartValueBucket(cartTotal),
      cart_currency: cartCurrency,
      cart_has_reserve: hasReserve ? "yes" : "no",
      cart_country: selectedCountry || "unknown",
      cart_total_value: Number(cartTotal.toFixed(2)),
    };
    trackAnalyticsEvent("cart_open", {
      params: cartSummary,
      clarityTags: cartSummary,
    });
    trackAnalyticsEvent(itemCount > 0 ? "cart_view_nonempty" : "cart_view_empty", {
      params: cartSummary,
    });
  }, [cartCurrency, cartTotal, hasReserve, isOpen, itemCount, selectedCountry]);

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      trackAnalyticsEvent("cart_checkout_empty", {
        params: {
          cart_item_count: 0,
          cart_country: selectedCountry || "unknown",
        },
      });
      alert(t("lukkey.cart.empty"));
      return;
    }

    const checkoutSummary = {
      checkout_item_count: itemCount,
      checkout_value_bucket: getCartValueBucket(cartTotal),
      checkout_currency: cartCurrency,
      checkout_has_reserve: hasReserve ? "yes" : "no",
      checkout_country: selectedCountry || "unknown",
      checkout_total_value: Number(cartTotal.toFixed(2)),
    };
    trackAnalyticsEvent("cart_checkout_click", {
      params: checkoutSummary,
      clarityTags: checkoutSummary,
    });
    trackAnalyticsEvent("begin_checkout_redirect", {
      params: checkoutSummary,
    });

    try {
      const lineItems = cartItems
        .map((item) => ({
          variantId: normalizeVariantId(item.variantId),
          quantity: isReserveItem(item) ? 1 : item.quantity || 1,
        }))
        .filter((item) => item.variantId);

      if (!lineItems.length) {
        trackAnalyticsEvent("cart_checkout_fallback_product_page", {
          params: checkoutSummary,
        });
        window.location.href = appendShopifyCheckoutLocale(
          SHOPIFY_PRODUCT_PAGE_URL,
          window.location.pathname
        ).toString();
        return;
      }

      const checkoutPath = lineItems
        .map((item) => `${item.variantId}:${item.quantity}`)
        .join(",");
      const checkoutUrl = new URL(
        `${SHOPIFY_CHECKOUT_BASE_URL}/${checkoutPath}`,
      );
      checkoutUrl.searchParams.set(
        "checkout[shipping_address][country]",
        String(selectedCountry || "US").trim().toUpperCase(),
      );
      window.location.href = appendCheckoutReturnParams(
        appendShopifyCheckoutLocale(checkoutUrl, window.location.pathname),
        window.location.pathname
      ).toString();
    } catch {
      trackAnalyticsEvent("cart_checkout_fallback_cart", {
        params: checkoutSummary,
      });
      const checkoutUrl = new URL(SHOPIFY_CHECKOUT_BASE_URL);
      checkoutUrl.searchParams.set(
        "checkout[shipping_address][country]",
        String(selectedCountry || "US").trim().toUpperCase(),
      );
      window.location.href = appendCheckoutReturnParams(
        appendShopifyCheckoutLocale(checkoutUrl, window.location.pathname),
        window.location.pathname
      ).toString();
    }
  };

  return (
    <div
      className={`fixed top-0 right-0 h-full bg-white z-[3000] shadow-lg transform transition-transform duration-300 ease-in-out
${isOpen ? "translate-x-0" : "translate-x-full"} 
w-full max-w-[600px]`}
      data-clarity-region="cart-drawer"
      data-clarity-state={isOpen ? "open" : "closed"}
    >
      <div className="p-6 flex flex-col h-full">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xl font-bold">{t("lukkey.cart.title")}</h2>
          <button
            type="button"
            onClick={() => {
              trackAnalyticsEvent("cart_close", {
                params: {
                  cart_item_count: itemCount,
                  cart_value_bucket: getCartValueBucket(cartTotal),
                },
              });
              onClose?.();
            }}
            className="ml-4 text-2xl leading-none"
            aria-label={t("lukkey.cart.close")}
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {cartItems.length === 0 ? (
            <div className="flex h-full min-h-[320px] items-center justify-center py-10">
              <p className="text-center text-neutral-500">{t("lukkey.cart.empty")}</p>
            </div>
          ) : (
            cartItems.map((item, index) => (
              <div
                key={index}
                className="py-4"
                data-clarity-region="cart-line-item"
                data-cart-item-kind={isReserveItem(item) ? "reserve" : "product"}
              >
                <div className="flex gap-4">
                  <img
                    src={getItemImage(item)}
                    alt={item.name}
                    className="h-20 w-20 rounded-lg bg-gray-100 object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="truncate text-base font-semibold">
                        {item.name}
                      </h3>
                      <span className="shrink-0 text-base font-semibold">
                        {formatMoney(
                          getItemPrice(item) * (item.quantity || 1),
                          getItemCurrency(item),
                          getItemLocale(item),
                        )}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      {item.color ? (
                        <span className="mr-2 text-xs text-neutral-500">
                          {item.color}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() =>
                          onUpdateQuantity?.(index, (item.quantity || 1) - 1)
                        }
                        className="h-8 w-8 rounded-full border border-gray-300 text-lg leading-none"
                        aria-label="Decrease quantity"
                      >
                        -
                      </button>
                      <span className="w-8 text-center text-sm font-medium">
                        {item.quantity || 1}
                      </span>
                      <button
                        type="button"
                        disabled={isReserveItem(item)}
                        onClick={() =>
                          onUpdateQuantity?.(index, (item.quantity || 1) + 1)
                        }
                        className={`h-8 w-8 rounded-full border text-lg leading-none ${
isReserveItem(item)
? "cursor-not-allowed border-gray-200 text-neutral-300"
: "border-gray-300"
}`}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveItem?.(index)}
                        className="ml-2 text-sm text-neutral-500 underline hover:text-black"
                      >
                        {t("lukkey.cart.delete")}
                      </button>
                    </div>
                  </div>
                </div>
                {index === 0 ? (
                  <CurrencyMarketSelect
                    t={t}
                    value={selectedCountry}
                    options={countryOptions}
                    onChange={(nextCountry) => {
                      trackAnalyticsEvent("cart_country_change", {
                        params: {
                          cart_country: nextCountry || "unknown",
                          cart_item_count: itemCount,
                          cart_value_bucket: getCartValueBucket(cartTotal),
                        },
                        clarityTags: {
                          cart_country: nextCountry || "unknown",
                        },
                      });
                      onCountryChange?.(nextCountry);
                    }}
                    wrapperClassName="mt-4 mb-4 flex-wrap rounded-2xl border border-[#e8e8eb] bg-[#f6f6f7] px-4 py-3"
                    labelClassName="text-neutral-500"
                    selectClassName="border-[#d7d7dc] bg-white text-black"
                  />
                ) : null}
                {Array.isArray(item.details) && item.details.length > 0 ? (
                  <div className="mt-4 rounded-2xl border border-[#e8e8eb] bg-[#f6f6f7] p-4">
                    <p className="text-sm font-semibold text-black">
                      {item.detailsTitle || t("lukkey.cart.reserveDetailsTitle")}
                    </p>
                    {item.detailsSummary ? (
                      <p className="mt-2 text-sm leading-6 text-neutral-500">
                        {item.detailsSummary}
                      </p>
                    ) : null}
                    <ul className="mt-3 space-y-2 text-sm leading-6 text-neutral-500">
                      {item.details.map((detail) => (
                        <li key={`${item.cartKey || item.name}-${detail}`} className="flex gap-2">
                          <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-black" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ))
          )}
          {cartItems.length > 0 ? <CartPriceCallout t={t} /> : null}

          {false && variantOptions.length > 0 ? (
            <div className="mt-6 border-t border-gray-200 pt-6">
              <p className="mb-4 text-lg font-semibold">
                {t("lukkey.cart.moreColors")}
              </p>
              <div className="grid grid-cols-1 gap-4">
                {variantOptions.map((option, idx) => (
                  <div
                    key={option.cartKey || option.variantId || `${option.color}-${idx}`}
                    className="rounded-xl border border-gray-200 p-3"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex items-center gap-4">
                        <img
                          src={getItemImage(option)}
                          alt={`${option.name} ${option.color || ""}`.trim()}
                          className="h-14 w-14 shrink-0 rounded-lg bg-gray-100 object-cover"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">
                            {option.name}
                          </p>
                          <p className="flex items-center gap-2 text-xs text-neutral-500">
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-full border border-gray-300"
                              style={{ backgroundColor: getColorDot(option.color) }}
                            />
                            {option.color}
                          </p>
                          <p className="mt-1 text-sm font-medium">
                            {formatMoney(
                              getItemPrice(option),
                              option.currency || "USD",
                              option.locale,
                            )}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled
                        className="cursor-not-allowed rounded-full border border-gray-300 px-4 py-1.5 text-sm font-medium text-neutral-500"
                      >
                        {t("lukkey.cart.comingSoon")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="mt-6">
          <div className="flex justify-between text-lg font-semibold">
            <span>{t("lukkey.cart.total")}</span>
            <span>
              {formatMoney(cartTotal, cartCurrency, cartLocale)}
            </span>
          </div>
          <button
            onClick={handleCheckout}
            className="mt-4 w-full bg-black text-white py-3 rounded-full"
            data-clarity-region="cart-checkout"
          >
            {t("lukkey.cart.checkout")}
          </button>
        </div>
      </div>
    </div>
  );
}
